import {
    Box,
    Button,
    Container,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    useMediaQuery
} from '@mui/material';
import React, {useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import i18next, { getBrowserLanguage } from '../../lang';
import {Category, Product, ProductSet} from "../admin/Product/Interfaces";
import {getTimeSlots} from "./DeliveryDateTime";
import ConfirmDialog from "../../components/ConfirmDialog";
import axios from "axios";
import {rootStore} from "../../stores/RootStore";
import {Download as DownloadIcon} from "@mui/icons-material";
import * as XLSX from 'xlsx';
import PurchaseStepper from "../../components/PurchaseStepper";
import { DeliveryData, OrderQuantity } from './interfaces';

interface LocationState {
    orderData: {
        deliveryData: DeliveryData[];
        orderQuantities: OrderQuantity[];
    };
    excelData: {

    },
    products: Product[],
    productSets: ProductSet[],
    categories: Category[],
    hasTempOrder?: boolean;
}

const OrderFinalConfirm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { authStore, viewStatusStore, appConfigStore } = rootStore;
    const [isCancelOrderDialogOpen, setIsCancelOrderDialogOpen] = useState(false);
    const [isCompletedOrderDialogOpen, setIsCompletedOrderDialogOpen] = useState(false);
    const [isErrorOrderDialogOpen, setIsErrorOrderDialogOpen] = useState(false);
    const [isConfirmOrderDialogOpen, setIsConfirmOrderDialogOpen] = useState(false);
    const [isOrdering, setIsOrdering] = useState(false);
    const [errorOrderMessage, setErrorOrderMessage] = useState('');
    const locationState = location.state as LocationState | null;
    const orderData = locationState?.orderData;
    const excelData = locationState?.excelData;
    const hasTempOrder = locationState?.hasTempOrder ?? false;

    useEffect(() => {
        if (!locationState) {
            navigate('/', { replace: true });
        }
    }, [locationState, navigate]);

    if (!locationState) return null;

    const getOrderQuantitiesByClassroom = (classroomId: string) => {
        return orderData.orderQuantities.flatMap(orderQuantity =>
            orderQuantity.books
                .filter(book => book.quantity[classroomId] !== undefined && book.quantity[classroomId] > 0)
                .map(book => ({
                    id: book.id,
                    type: orderQuantity.type,
                    title: book.title,
                    price: book.price,
                    quantity: book.quantity[classroomId]
                }))
        );
    };

    const getSubtotal = (classroomId: string) => {
        return getOrderQuantitiesByClassroom(classroomId).reduce((total, book) => total + (book.price * book.quantity), 0);
    };

    const formatDate = (date: Date | null) => {
        if (!date) return i18next.t('common.unspecified');
        const lang = getBrowserLanguage();
        const localeMap: Record<string, string> = {
            ja: 'ja-JP', en: 'en-US', ko: 'ko-KR', hi: 'hi-IN', de: 'de-DE'
        };
        return new Date(date).toLocaleDateString(localeMap[lang] || 'ja-JP', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            weekday: 'short'
        });
    };


    const getTimeSlot = (timeSlotId: number | null) => {
        if (!timeSlotId) return i18next.t('common.unspecified');
        const slot = getTimeSlots().find(slot => slot.id === timeSlotId);
        return slot ? slot.time : '';
    };

    const handleCancelOrder = (tempOrderDeleted = false) => {
        const effectiveHasTempOrder = tempOrderDeleted ? false : hasTempOrder;
        setIsCancelOrderDialogOpen(false);
        if (authStore.user.level === 'CLASSROOM_MANAGER') {
            navigate('/purchase/delivery-datetime', { state: { hasTempOrder: effectiveHasTempOrder } });
        } else {
            navigate('/purchase/delivery-address', { state: { hasTempOrder: effectiveHasTempOrder } });
        }
    };

    const handleCancelOrderWithDeleteTemp = async () => {
        try {
            await axios.delete('/api/v1/orders/temp');
        } catch (error) {
            console.error('Failed to delete temp order:', error);
        }
        handleCancelOrder(true);
    };

    const handleCompletedOrder = () => {
        setIsCompletedOrderDialogOpen(false);

        if (authStore.user.level !== 'CLASSROOM_MANAGER') {
            navigate('/purchase/delivery-address', {});
        } else {
            navigate('/purchase/delivery-datetime', {});
        }
    }

    const handleOrder = async () => {
        if (isOrdering) return;
        setIsOrdering(true);
        setIsConfirmOrderDialogOpen(false);
        try {
            viewStatusStore.setLoadingStatus({ open: true });

            const transformedData = orderData.deliveryData.map(({ classroomName, phone, userName, ...delivery }) => {
                const orders = orderData.orderQuantities.flatMap(orderQuantity =>
                    orderQuantity.books
                        .filter(book => book.quantity[delivery.id] && book.quantity[delivery.id] > 0)
                        .map(book => ({
                            id: book.id,
                            type: orderQuantity.type,
                            title: book.title,
                            price: book.price,
                            quantity: book.quantity[delivery.id]
                        }))
                );

                // deliveryDate를 LocalDateTime 형태로 변환
                let formattedDeliveryDate = null;
                if (delivery.deliveryDate) {
                    if (excelData) {
                        // excelData가 존재하는 경우: 브라우저 타임존 값을 UTC+0으로 변환
                        const date = new Date(delivery.deliveryDate);
                        // 로컬 타임존 오프셋을 구해서 UTC로 변환 (현재 로컬 시간을 UTC로 변환)
                        const utcDate = new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
                        formattedDeliveryDate = utcDate.toISOString().slice(0, 19); // YYYY-MM-DDTHH:mm:ss 형태
                    } else {
                        // excelData가 없는 경우: 기존 로직 유지
                        formattedDeliveryDate = new Date(delivery.deliveryDate).toISOString().slice(0, 19);
                    }
                }

                return {
                    ...delivery,
                    id: delivery.userId ?? delivery.id,  // fix/multi-address-quantity-bug で id が addressId に変わったため、バックエンドが期待する userId で上書き
                    deliveryDate: formattedDeliveryDate,
                    orders
                };
            });

            await axios.post('/api/v1/orders', transformedData);
            setIsCompletedOrderDialogOpen(true);
        } catch (error: any) {
            console.error('Failed to order request:', error);
            if (error.response?.data?.errorCode === 'InvalidProduct') {
                setErrorOrderMessage(error.response.data.message);
                setIsErrorOrderDialogOpen(true);
            }
        } finally {
            viewStatusStore.setLoadingStatus({ open: false });
            setIsOrdering(false);
        }
    }

    const handleDownloadOrders = () => {
        // 엑셀 헤더 정의
        const headers = [
            '*' + i18next.t('order.classroom'),
            '*' + i18next.t('order.textbook_name'),
            i18next.t('product.volume'),
            i18next.t('product.subject_code'),
            '*' + i18next.t('order.quantity'),
            i18next.t('order.delivery_date'),
            i18next.t('order.time_slot')
        ];

        let excelDataArray: any[][];

        if (excelData && Array.isArray(excelData)) {
            // 일괄 업로드 경로: excelData 사용
            excelDataArray = excelData.map((item: any) => {
                const timeSlotValue = item.timeSlot ?
                    getTimeSlots().find(slot => slot.id === item.timeSlot)?.time || item.timeSlot : '';
                return [
                    item.classroomName || '',
                    item.productTitle || '',
                    item.productVolume || '',
                    item.subjectCode || '',
                    item.quantity || 0,
                    item.deliveryDate || '',
                    timeSlotValue
                ];
            });
        } else {
            // 일반 주문 경로: orderData로부터 생성
            excelDataArray = [];
            for (const delivery of orderData.deliveryData) {
                const items = getOrderQuantitiesByClassroom(delivery.id);
                const deliveryDateStr = delivery.deliveryDate
                    ? new Date(delivery.deliveryDate).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' })
                    : '';
                const timeSlotValue = delivery.deliveryTime
                    ? getTimeSlots().find(slot => slot.id === delivery.deliveryTime)?.time || ''
                    : '';
                for (const item of items) {
                    excelDataArray.push([
                        delivery.classroomName || '',
                        item.title || '',
                        '',
                        '',
                        item.quantity || 0,
                        deliveryDateStr,
                        timeSlotValue
                    ]);
                }
            }
        }

        // 워크시트 생성
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelDataArray]);

        // 워크북 생성
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, '주문 목록');

        // 파일명 생성 (현재 날짜 포함)
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `${i18next.t('purchase.order_list_title')}_${currentDate}.xlsx`;

        // 엑셀 파일 다운로드
        XLSX.writeFile(workbook, fileName);
    }

    return (
        <Container maxWidth={false} sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, md: 3 }, width: '100%' }}>
            <PurchaseStepper activeStep={4} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 1, md: 0 } }}>
                        <Typography variant="h6" gutterBottom>
                            {i18next.t('purchase.final_confirm')}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            sx={{
                                fontSize: '0.9rem',
                                height: '35px',
                                minWidth: '110px',
                                backgroundColor: 'primary.main',
                                mr: 2,
                                boxShadow: 1,
                                '&:hover': { backgroundColor: 'primary.dark' }
                            }}
                            onClick={handleDownloadOrders}
                        >
                            {i18next.t('purchase.batch_download_orders')}
                        </Button>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        mt: '20px'
                    }}>
                        <Paper sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: 'none',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#fff'
                        }}>
                            {orderData.deliveryData.map((delivery, index) => (
                                <Box key={delivery.id} sx={{ mb: 4 }}>
                                    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                                        <Table sx={{
                                            '& .MuiTableCell-root': {
                                                borderRight: '1px solid #f0f0f0',
                                                borderBottom: '1px solid #f0f0f0',
                                                padding: '0 8px',
                                                height: '40px',
                                                whiteSpace: 'nowrap'
                                            }
                                        }} size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '3% !important',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('common.number')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '200px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('order.classroom')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '300px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('address.address')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('contact.phone')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('purchase.person_in_charge')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('order.delivery_date')}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        backgroundColor: '#f5f5f5',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 800,
                                                        width: '130px',
                                                        textAlign: 'center',
                                                        height: '40px !important'
                                                    }}>
                                                        {i18next.t('order.time_slot')}
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                <TableRow>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {delivery.classroomName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem' }}>
                                                        {delivery.address}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {delivery.phone}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {delivery.userName}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {formatDate(delivery.deliveryDate)}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {getTimeSlot(delivery.deliveryTime)}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                    <TableCell sx={{ height: '2px !important' }} />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell />
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.textbook_name')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('product.price')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.quantity')}
                                                    </TableCell>
                                                    <TableCell />
                                                    <TableCell />
                                                </TableRow>
                                                {getOrderQuantitiesByClassroom(delivery.id).map((orderQuantity, index) => (
                                                    <TableRow key={`${orderQuantity.type}-${orderQuantity.id}`}>
                                                        <TableCell />
                                                        <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                            {index + 1}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.7rem' }}>
                                                            {orderQuantity.title}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                            {i18next.t('common.price_yen', { price: orderQuantity.price })}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                            {orderQuantity.quantity}
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                            {i18next.t('common.price_yen', { price: orderQuantity.price * orderQuantity.quantity })}
                                                        </TableCell>
                                                        <TableCell />
                                                    </TableRow>
                                                ))}
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.subtotal')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('common.price_yen', { price: getSubtotal(delivery.id) })}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('user.shipping_fee')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('common.price_yen', { price: (getSubtotal(delivery.id) === 0 ? 0 : delivery.postage) })}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('order.tax')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                                                        {i18next.t('common.price_yen', { price: Math.floor((getSubtotal(delivery.id) + (getSubtotal(delivery.id) === 0 ? 0 : delivery.postage)) * appConfigStore.taxRate) })}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell /><TableCell /><TableCell /><TableCell />
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {i18next.t('order.total')}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: '0.7rem', textAlign: 'center', fontWeight: 'bold' }}>
                                                        {(() => {
                                                            const sub = getSubtotal(delivery.id);
                                                            const shipping = sub === 0 ? 0 : delivery.postage;
                                                            const tax = Math.floor((sub + shipping) * appConfigStore.taxRate);
                                                            return i18next.t('common.price_yen', { price: sub + shipping + tax });
                                                        })()}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}
                        </Paper>
                    </Box>
                    <Box sx={{
                        p: 1,
                        backgroundColor: '#fff',
                        borderTop: '1px solid #e0e0e0',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 1,
                        minHeight: '35px',
                        flexShrink: 0
                    }}>
                        {!excelData ?
                            <Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        fontSize: '0.8rem',
                                        height: '28px',
                                        minWidth: '80px',
                                        mr: { xs: 0, md: 1 },
                                        width: { xs: '100%', md: 'auto' }
                                    }}
                                    onClick={() => navigate('/purchase/order-confirm', {
                                        state: {
                                            orderData: locationState.orderData,
                                            products: locationState.products,
                                            productSets: locationState.productSets,
                                            categories: locationState.categories,
                                            userProductMappings: (locationState as any).userProductMappings,
                                            hasTempOrder
                                        }
                                    })}
                                >
                                    {i18next.t('purchase.go_to_order_confirm')}
                                </Button>
                            </Box>
                            :
                            <Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}} />
                        }
                        <Box sx={{
                            p: { xs: 0, md: 1 },
                            backgroundColor: '#fff',
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'flex-end',
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: 1,
                            minHeight: '35px',
                            flexShrink: 0
                        }}>
                            <Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
                                <Button
                                    variant="outlined"
                                    sx={{
                                        fontSize: '0.8rem',
                                        height: '28px',
                                        minWidth: '80px',
                                        mr: { xs: 0, md: 1 },
                                        width: { xs: '100%', md: 'auto' }
                                    }}
                                    onClick={() => setIsCancelOrderDialogOpen(true)}
                                >
                                    {i18next.t('order.order_cancelled')}
                                </Button>
                            </Box>
                            <Box style={{marginTop: 20, display: 'flex', justifyContent: 'flex-start'}}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        fontSize: '0.8rem',
                                        height: '28px',
                                        minWidth: '80px',
                                        mr: { xs: 0, md: 1 },
                                        backgroundColor: 'primary.main',
                                        width: { xs: '100%', md: 'auto' },
                                        '&:hover': {
                                            backgroundColor: 'primary.dark'
                                        }
                                    }}
                                    onClick={() => setIsConfirmOrderDialogOpen(true)}
                                >
                                    {i18next.t('order.order_confirmed')}
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <ConfirmDialog
                open={isCancelOrderDialogOpen}
                onClose={() => setIsCancelOrderDialogOpen(false)}
                onConfirm={handleCancelOrder}
                title={i18next.t('order.order_cancelled')}
                message={i18next.t('common.confirm_cancel')}
        				button1Text={i18next.t('common.no')}
        				button2Text={i18next.t('common.yes')}
                onConfirmAlt={hasTempOrder ? handleCancelOrderWithDeleteTemp : undefined}
                confirmAltText={hasTempOrder ? i18next.t('purchase.yes_delete_temp_order') : undefined}
                confirmAltColor="error"
            />
            <ConfirmDialog
                open={isCompletedOrderDialogOpen}
                onClose={null}
                onConfirm={handleCompletedOrder}
                title={i18next.t('purchase.order_complete')}
                message={i18next.t('purchase.order_completed_message')}
                button1Text={null}
                button2Text={i18next.t('common.confirm')}
            />
            <ConfirmDialog
                open={isErrorOrderDialogOpen}
                onClose={null}
                onConfirm={() => setIsErrorOrderDialogOpen(false)}
                title={i18next.t('purchase.order_failed')}
                message={`${i18next.t('purchase.order_failed_message')}\n\n${errorOrderMessage}`}
                button1Text={null}
                button2Text={i18next.t('common.confirm')}
            />
            <ConfirmDialog
                open={isConfirmOrderDialogOpen}
                onClose={() => setIsConfirmOrderDialogOpen(false)}
                onConfirm={handleOrder}
                title={i18next.t('order.order_confirmed')}
                message={i18next.t('purchase.confirm_order')}
                button1Text={i18next.t('common.cancel')}
                button2Text={i18next.t('purchase.confirm_order_button')}
            />
        </Container>
    );
};

export default OrderFinalConfirm;